import styled from "styled-components";
import { useEffect, useState } from "react";
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    format,
    addMonths,
    subMonths,
    isSameMonth,
} from "date-fns";
import AdminService from "../../../services/admin.service";
import { useGlobal } from "../../../context/global";
import { Admin, CashShift, Seller, ShiftPeriod, ShiftRole } from "../../../types/common.types";

interface MonthShiftGridProps {
    role: ShiftRole
}

function chunkDays(days: Date[]): Date[][] {
    const weeks: Date[][] = []
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7))
    return weeks
}

export default function MonthShiftGrid({ role }: MonthShiftGridProps) {
    const { setIsLoading } = useGlobal()
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [sellers, setSellers] = useState<Seller[]>([])
    const [admins, setAdmins] = useState<Admin[]>([])
    const [shifts, setShifts] = useState<CashShift[]>([])
    const [openCell, setOpenCell] = useState<{ date: Date, period: ShiftPeriod } | null>(null)

    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 })
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
    const weeks = chunkDays(days)
    const periods: ShiftPeriod[] = role === 'seller' ? ['AM', 'PM'] : ['FULL']
    const people: { id: string, name: string }[] = role === 'seller' ? sellers : admins

    useEffect(() => {
        AdminService.listSellers().then(res => setSellers(res.data)).catch(console.log)
        AdminService.listAdmins().then(res => setAdmins(res.data)).catch(console.log)
    }, [])

    async function reloadShifts() {
        setIsLoading(true)
        try {
            const res = await AdminService.listCashShifts(gridStart, gridEnd)
            setShifts((res.data as CashShift[]).filter(s => s.role === role))
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        reloadShifts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMonth.getMonth(), currentMonth.getFullYear(), role])

    // OJO: no comparar con `isSameDay(new Date(s.shift_date), date)` — s.shift_date llega desde la
    // API como ISO en medianoche UTC (ej. "2026-08-24T00:00:00.000Z"), y date-fns compara por
    // calendario LOCAL: en una zona negativa (America/Santiago) esa medianoche UTC cae en el día
    // anterior localmente, corriendo el turno un día para atrás en el grid aunque en la base quedó
    // bien guardado. Comparar el string de fecha directo evita el problema por completo.
    function shiftsFor(date: Date, period: ShiftPeriod) {
        const dayKey = format(date, 'yyyy-MM-dd')
        return shifts.filter(s => String(s.shift_date).slice(0, 10) === dayKey && s.period === period)
    }

    async function togglePerson(date: Date, period: ShiftPeriod, person_id: string) {
        const existing = shiftsFor(date, period).find(s => (role === 'admin' ? s.admin_id : s.seller_id) === person_id)
        setIsLoading(true)
        try {
            if (existing) await AdminService.deleteCashShift(existing.id)
            else await AdminService.upsertCashShift({
                shift_date: date, period, role,
                seller_id: role === 'seller' ? person_id : undefined,
                admin_id: role === 'admin' ? person_id : undefined,
                in_agency: role === 'seller' ? true : false
            })
            await reloadShifts()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    async function updateInAgencyPeriod(date: Date, period: ShiftPeriod, admin_id: string, value: string) {
        setIsLoading(true)
        try {
            await AdminService.upsertCashShift({
                shift_date: date, period, role: 'admin', admin_id,
                in_agency: value !== '',
                in_agency_period: value === '' ? null : value as ShiftPeriod
            })
            await reloadShifts()
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    function openPopover(date: Date, period: ShiftPeriod) {
        setOpenCell({ date, period })
    }

    return (
        <Main>
            <div className="nav">
                <button type="button" onClick={() => setCurrentMonth(m => subMonths(m, 1))}>{'<'}</button>
                <span>{format(currentMonth, 'MMMM yyyy')}</span>
                <button type="button" onClick={() => setCurrentMonth(m => addMonths(m, 1))}>{'>'}</button>
            </div>
            <Grid>
                <div className="head-row">
                    <div className="turno-label" />
                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                        <div className="day-head" key={d}>{d}</div>
                    ))}
                </div>
                {weeks.map((week, wIdx) => (
                    <div className="week" key={wIdx}>
                        <div className="week-dates">
                            <div className="turno-label">Semana</div>
                            {week.map((day, dIdx) => (
                                <div className={`day-number ${isSameMonth(day, currentMonth) ? '' : 'other-month'}`} key={dIdx}>
                                    {format(day, 'd')}
                                </div>
                            ))}
                        </div>
                        {periods.map(period => (
                            <div className="period-row" key={period}>
                                <div className="turno-label">{period === 'FULL' ? 'Turno' : `Turno ${period}`}</div>
                                {week.map((day, dIdx) => {
                                    const assigned = shiftsFor(day, period)
                                    return (
                                        <div
                                            className={`cell ${assigned.length ? 'filled' : ''}`}
                                            key={dIdx}
                                            onClick={() => openPopover(day, period)}
                                        >
                                            {assigned.map(s => s.person?.name || '?').join(', ') || '-'}
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                ))}
            </Grid>
            {openCell && (
                <Popover onClick={() => setOpenCell(null)}>
                    <div className="content" onClick={e => e.stopPropagation()}>
                        <h4>{format(openCell.date, 'dd/MM/yyyy')} — {openCell.period === 'FULL' ? 'Turno completo' : `Turno ${openCell.period}`}</h4>
                        <ul>
                            {people.length === 0 && <li className="empty">{role === 'admin' ? 'No hay administradores registrados' : 'No hay vendedores registrados'}</li>}
                            {people.map(person => {
                                const shift = shiftsFor(openCell.date, openCell.period).find(s => (role === 'admin' ? s.admin_id : s.seller_id) === person.id)
                                const checked = !!shift
                                return (
                                    <li key={person.id}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => togglePerson(openCell.date, openCell.period, person.id)}
                                            />
                                            {person.name}
                                        </label>
                                        {role === 'admin' && checked && (
                                            <select
                                                className="in-agency-select"
                                                value={shift?.in_agency_period || ''}
                                                onChange={e => updateInAgencyPeriod(openCell.date, openCell.period, person.id, e.target.value)}
                                            >
                                                <option value="">Home office (no presencial)</option>
                                                <option value="AM">Presencial AM</option>
                                                <option value="PM">Presencial PM</option>
                                                <option value="FULL">Presencial todo el día</option>
                                            </select>
                                        )}
                                    </li>
                                )
                            })}
                        </ul>
                        <button type="button" className="close" onClick={() => setOpenCell(null)}>Cerrar</button>
                    </div>
                </Popover>
            )}
        </Main>
    )
}

const Main = styled.div`
.nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;
    text-transform: capitalize;
    font-weight: 600;
    button {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: none;
        background: ${props => props.theme.white};
        box-shadow: 0 2px 4px ${props => props.theme.blackTransparent};
        cursor: pointer;
    }
}
`

const Grid = styled.div`
overflow-x: auto;
.head-row, .week-dates, .period-row {
    display: grid;
    grid-template-columns: 90px repeat(7, minmax(90px, 1fr));
}
.turno-label {
    font-size: 12px;
    font-weight: 600;
    padding: 6px;
    display: flex;
    align-items: center;
}
.day-head {
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    padding: 6px;
    background: ${props => props.theme.whiteBone};
}
.week {
    border-top: 2px solid ${props => props.theme.gray};
}
.day-number {
    text-align: center;
    font-size: 12px;
    color: ${props => props.theme.gray};
    padding: 4px;
}
.day-number.other-month {
    opacity: 0.4;
}
.period-row .cell {
    border: 1px solid ${props => props.theme.blackTransparent};
    min-height: 32px;
    font-size: 12px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 2px;
    word-break: break-word;
}
.period-row .cell:hover {
    background: ${props => props.theme.whiteBone};
}
.period-row .cell.filled {
    background: ${props => props.theme.blueLight};
    color: ${props => props.theme.white};
    font-weight: 600;
}
`

const Popover = styled.div`
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background: ${props => props.theme.blackTransparent};
z-index: 200;
display: flex;
align-items: center;
justify-content: center;
.content {
    background: ${props => props.theme.white};
    border-radius: 10px;
    padding: 20px;
    min-width: 250px;
    max-height: 80vh;
    overflow-y: auto;
    h4 {
        margin-bottom: 10px;
        font-size: 14px;
    }
    .empty {
        font-size: 13px;
        color: ${props => props.theme.gray};
    }
    ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 15px;
        li {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 8px;
        }
        label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 14px;
            cursor: pointer;
        }
        .in-agency-select {
            font-size: 12px;
            padding: 4px 6px;
            border-radius: 6px;
            border: 1px solid ${props => props.theme.gray};
        }
    }
    .close {
        width: 100%;
        padding: 8px;
        border-radius: 6px;
        border: 2px solid ${props => props.theme.blue};
        background: transparent;
        color: ${props => props.theme.blue};
        cursor: pointer;
    }
}
`
